import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "font-bold text-xl all-text-color" }
const _hoisted_2 = { class: "m-0 p-0" }
const _hoisted_3 = {
  key: 0,
  class: "chart col-fixed m-0 p-0 pb-1"
}
const _hoisted_4 = {
  key: 1,
  class: "my-3 w-full font-bold"
}
const _hoisted_5 = {
  key: 0,
  class: "col inline-grid"
}
const _hoisted_6 = {
  key: 1,
  class: "flex justify-content-between my-2 mx-4 dark-medium"
}
const _hoisted_7 = { class: "font-bold text-sm mr-5" }

import Chart from 'primevue/chart';
import ChartLegendItem from './ChartLegendItem.vue';
import { ref, watch } from 'vue';
import { useAttendanceStore } from '@/store';
import { storeToRefs } from 'pinia';
import { THERAPY_RESULT_TYPES_NAME } from '@/enum/calendar';

interface LabelInterface {
  title: string;
  color: string;
  textColor: string;
  numberColor: string;
  total: string;
  percentage: string;
  order: number;
}

interface DataInterface {
  name: string;
  percentage: number;
  count: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AttendanceChart',
  setup(__props) {

const { loading, results, resultsTotal, studentName } = storeToRefs(useAttendanceStore());

const labels = ref<LabelInterface[]>([]);
const titles = ref<string[]>([]);
const chartDataNumbers = ref<number[]>([]);
const chartOptions = ref({
  plugins: {
    legend: {
      display: false,
    },
  },
});
const chartData = ref({});
const colors = ref<string[]>([]);
const attendanceCheck = ref(0);
const getObjectStyleByTherapyResultName = (result: DataInterface): LabelInterface | null => {
  attendanceCheck.value += result.percentage;
  switch (result.name) {
    case THERAPY_RESULT_TYPES_NAME.PRESENT:
      return {
        order: 1,
        title: result.name,
        color: '#8BCFCF',
        textColor: '#045C5C',
        numberColor: '#078888',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.NO_SCHOOL:
      return {
        order: 2,
        title: result.name,
        color: '#40B3DD',
        textColor: '#26728E',
        numberColor: '#40B3DD',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.ABSENT:
    case THERAPY_RESULT_TYPES_NAME.NO_SHOW:
      return {
        order: 3,
        title: result.name,
        color: '#EDA745',
        textColor: '#94703C',
        numberColor: '#EDA745',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.LATE_CANCEL:
      return {
        order: 4,
        title: result.name,
        color: '#7E78D2',
        textColor: '#474299',
        numberColor: '#7E78D2',
        total: `${result.count}`,
        percentage: `${result.percentage}`,
      };
    case THERAPY_RESULT_TYPES_NAME.CLINICIAN_CANCELLED:
      return {
        order: 5,
        title: result.name,
        color: '#D49BC1',
        textColor: '#7F3666',
        numberColor: '#D49BC1',
        total: result.count.toString(),
        percentage: result.percentage.toString(),
      };
    case THERAPY_RESULT_TYPES_NAME.CUSTOMER_CANCELLED:
      return {
        order: 6,
        title: result.name,
        color: '#822508',
        textColor: '#c3370b',
        numberColor: '#822508',
        total: result.count.toString(),
        percentage: result.percentage.toString(),
      };
    default:
      return null;
  }
};

const setLabels = () => {
  attendanceCheck.value = 0;

  labels.value = results.value.map(
    (result: DataInterface) => getObjectStyleByTherapyResultName(result) as LabelInterface,
  );

  labels.value = labels.value.sort((a: LabelInterface, b: LabelInterface) => a.order - b.order);
  titles.value = labels.value.map((c: LabelInterface) => String(c.title));
  chartDataNumbers.value = labels.value.map((c: LabelInterface) => Number(c.total));
  colors.value = labels.value.map((c: LabelInterface) => c.color);
  chartData.value = {
    labels: titles.value,
    datasets: [
      {
        data: chartDataNumbers.value,
        backgroundColor: colors.value,
        borderWidth: 4,
        borderColor: '#e2f4f4',
        borderRadius: 5,
        id: 'chart',
      },
    ],
  };
};

watch(
  () => results.value,
  () => setLabels(),
);

return (_ctx: any,_cache: any) => {
  const _component_Loader = _resolveComponent("Loader")!

  return (!_unref(loading))
    ? (_openBlock(), _createElementBlock("div", { key: _unref(studentName) }, [
        _createElementVNode("p", _hoisted_1, _toDisplayString(_unref(studentName)), 1),
        _createElementVNode("div", _hoisted_2, [
          (attendanceCheck.value > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_unref(Chart), {
                  type: "doughnut",
                  data: chartData.value,
                  width: 100,
                  height: 100,
                  options: chartOptions.value
                }, null, 8, ["data", "options"])
              ]))
            : (_openBlock(), _createElementBlock("h5", _hoisted_4, "No data for " + _toDisplayString(_unref(studentName)), 1))
        ]),
        (attendanceCheck.value > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labels.value, (label) => {
                return (_openBlock(), _createBlock(ChartLegendItem, {
                  key: label.color,
                  title: label.title,
                  total: label.total,
                  color: label.color,
                  "text-color": label.textColor,
                  "number-color": label.numberColor,
                  percentage: label.percentage
                }, null, 8, ["title", "total", "color", "text-color", "number-color", "percentage"]))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "border-solid border-1 border-white" }, null, -1)),
        (attendanceCheck.value > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[0] || (_cache[0] = _createElementVNode("div", { class: "text-xs ml-1" }, "Total", -1)),
              _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(resultsTotal)), 1)
            ]))
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createBlock(_component_Loader, {
        key: 1,
        relative: true,
        class: "flex justify-content-center align-items-center h-full"
      }))
}
}

})